export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export class RetryService {
  private readonly config: RetryConfig;

  constructor(
    config: RetryConfig = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 10000,
      backoffMultiplier: 2,
    }
  ) {
    this.config = config;
  }

  async executeWithRetry<T>(
    operation: () => Promise<T>,
    context: string = "operation"
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 0; attempt <= this.config.maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (attempt === this.config.maxRetries) {
          console.error(
            `${context} failed after ${this.config.maxRetries} retries:`,
            lastError
          );
          throw lastError;
        }

        const delay = Math.min(
          this.config.baseDelay *
            Math.pow(this.config.backoffMultiplier, attempt),
          this.config.maxDelay
        );

        console.warn(
          `${context} failed (attempt ${attempt + 1}), retrying in ${delay}ms:`,
          lastError
        );
        await this.sleep(delay);
      }
    }

    throw lastError!;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
